import { z } from 'zod';
import { amountMinorSchema, uuidSchema } from './common';

export const kitchenStationSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  is_default: z.boolean(),
  active: z.boolean(),
  sort_order: z.number().int(),
});
export type KitchenStation = z.infer<typeof kitchenStationSchema>;

export const menuCategorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sort_order: z.number().int(),
  default_station_id: uuidSchema.nullable(),
  active: z.boolean(),
});
export type MenuCategory = z.infer<typeof menuCategorySchema>;

export const modifierOptionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  price_delta_minor: amountMinorSchema,
  active: z.boolean(),
  sort_order: z.number().int(),
});
export type ModifierOption = z.infer<typeof modifierOptionSchema>;

export const modifierGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  min_select: z.number().int().min(0),
  max_select: z.number().int().min(1),
  active: z.boolean(),
  options: z.array(modifierOptionSchema),
});
export type ModifierGroup = z.infer<typeof modifierGroupSchema>;

/** https only; external images are hotlinked (e.g. Unsplash) until R2 uploads arrive. */
const httpsUrlSchema = z.url({ protocol: /^https$/ }).max(1000);

export const menuItemImageSchema = z.object({
  url: z.string(),
  /** Photographer / source credit, shown wherever the image is shown large. */
  credit_name: z.string().nullable(),
  credit_url: z.string().nullable(),
});
export type MenuItemImage = z.infer<typeof menuItemImageSchema>;

export const menuItemSchema = z.object({
  id: uuidSchema,
  category_id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  price_minor: amountMinorSchema,
  currency: z.string(),
  station_id: uuidSchema.nullable(),
  prep_minutes: z.number().int().nullable(),
  image: menuItemImageSchema.nullable(),
  /** false = sold out / unavailable for ordering. */
  available: z.boolean(),
  active: z.boolean(),
  sort_order: z.number().int(),
  modifier_group_ids: z.array(uuidSchema),
  version: z.number().int(),
});
export type MenuItem = z.infer<typeof menuItemSchema>;

/** Whole-branch catalog in one payload (cached by POS/Service, spec §9.1). */
export const catalogResponseSchema = z.object({
  currency: z.string(),
  categories: z.array(menuCategorySchema),
  items: z.array(menuItemSchema),
  modifier_groups: z.array(modifierGroupSchema),
  stations: z.array(kitchenStationSchema),
});
export type Catalog = z.infer<typeof catalogResponseSchema>;

/*
 * Update schemas are built from field shapes WITHOUT `.default()`: in zod 4 `.partial()` keeps
 * defaults, which would silently reset omitted fields on PATCH.
 */
const stationFields = {
  name: z.string().trim().min(1).max(60),
  is_default: z.boolean(),
  sort_order: z.number().int().min(0),
};
export const createStationRequestSchema = z.object({
  ...stationFields,
  is_default: stationFields.is_default.default(false),
  sort_order: stationFields.sort_order.default(0),
});
export const updateStationRequestSchema = z
  .object({ ...stationFields, active: z.boolean() })
  .partial();

const categoryFields = {
  name: z.string().trim().min(1).max(60),
  sort_order: z.number().int().min(0),
  default_station_id: uuidSchema.nullable(),
};
export const createCategoryRequestSchema = z.object({
  ...categoryFields,
  sort_order: categoryFields.sort_order.default(0),
  default_station_id: categoryFields.default_station_id.default(null),
});
export const updateCategoryRequestSchema = z
  .object({ ...categoryFields, active: z.boolean() })
  .partial();

const menuItemFields = {
  category_id: uuidSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable(),
  price_minor: amountMinorSchema.refine((v) => v >= 0, 'Price cannot be negative'),
  station_id: uuidSchema.nullable(),
  prep_minutes: z.number().int().min(0).max(240).nullable(),
  sort_order: z.number().int().min(0),
  modifier_group_ids: z.array(uuidSchema).max(20),
  image_url: httpsUrlSchema.nullable(),
  image_credit_name: z.string().trim().max(120).nullable(),
  image_credit_url: httpsUrlSchema.nullable(),
};
export const createMenuItemRequestSchema = z.object({
  ...menuItemFields,
  description: menuItemFields.description.default(null),
  station_id: menuItemFields.station_id.default(null),
  prep_minutes: menuItemFields.prep_minutes.default(null),
  sort_order: menuItemFields.sort_order.default(0),
  modifier_group_ids: menuItemFields.modifier_group_ids.default([]),
  image_url: menuItemFields.image_url.default(null),
  image_credit_name: menuItemFields.image_credit_name.default(null),
  image_credit_url: menuItemFields.image_credit_url.default(null),
});
export const updateMenuItemRequestSchema = z
  .object({ ...menuItemFields, available: z.boolean(), active: z.boolean() })
  .partial()
  .extend({ expected_version: z.number().int().nonnegative() });

export const modifierOptionInputSchema = z.object({
  /** Present to update an existing option; omitted to create one. */
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(60),
  price_delta_minor: amountMinorSchema,
  active: z.boolean().default(true),
  sort_order: z.number().int().min(0).default(0),
});
export const upsertModifierGroupRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    min_select: z.number().int().min(0).max(20).default(0),
    max_select: z.number().int().min(1).max(20).default(1),
    active: z.boolean().default(true),
    /** Full option list; options missing from the list are deactivated (never deleted). */
    options: z.array(modifierOptionInputSchema).min(1).max(40),
  })
  .refine((g) => g.min_select <= g.max_select, {
    message: 'min_select must be ≤ max_select',
    path: ['min_select'],
  });
